import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Empty } from "antd";
import { ColumnsType } from "antd/es/table";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
  CustomTable,
  CustomTextField,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { ReviewStatusTag } from "../ServiceRequestDetailsPage/components";
import {
  compareIsoDates,
  dateToLocalTime,
  getRouteWithParams,
  ROUTES,
} from "~/utils";
import * as APITypes from "~/API";
import { ServiceRequestTablePageLoaderType } from "./loaders/ServiceRequestTablePageLoader";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { TFunction } from "i18next";

export const getServiceRequestTableColumns = (
  t: TFunction
): ColumnsType<APITypes.ServiceRequest> => [
      {
        title: t("Title"),
        dataIndex: "title",
        key: "title",
        width: 250,
        sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        render: (_, record: APITypes.ServiceRequest) => {
          const id = record.id;
          return (
            <a
              href={getRouteWithParams("serviceRequestDetails", {
                requestID: id,
              })}
            >
              {record.title}
            </a>
          );
        },
      },
      {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        width: 200,
        sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
        render: (_, record: APITypes.ServiceRequest) => {
          const status = record.status;
          return <ReviewStatusTag status={status} />;
        },
      },
      {
        title: t("Handler"),
        dataIndex: "handlerName",
        key: "handlerName",
        width: 200,
        render: (_, record: APITypes.ServiceRequest) => {
          const handlerName = record.handlerName;
          return (
            <CustomTextField prefix={<UserOutlined />} value={handlerName} />
          );
        },
      },
      {
        title: <>{t("Created_At")} </>,
        dataIndex: "createdAt",
        key: "createdAt",
        width: 200,
        sorter: (a, b) => compareIsoDates(a.createdAt, b.createdAt),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "descend",
        render: (_, record: APITypes.ServiceRequest) => {
          return (
            <CustomTextField
              value={dateToLocalTime(record.createdAt, "dateTime")}
              onEmpty="n/a"
            />
          );
        },
      },
      {
        title: <>{t("Updated_At")} </>,
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 200,
        sorter: (a, b) => compareIsoDates(a.updatedAt, b.updatedAt),
        render: (_, record: APITypes.ServiceRequest) => {
          return (
            <CustomTextField
              value={dateToLocalTime(record.updatedAt, "dateTime")}
              onEmpty="n/a"
            />
          );
        },
      },
      {
        title: t("Type"),
        dataIndex: "type",
        key: "type",
        width: 200,
        sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
        render: (_, record: APITypes.ServiceRequest) => {
          return <CustomTextField value={record.type} />;
        },
      },
    ];

export const ServiceRequestTablePage = () => {
  const loaderData = useLoaderData() as ServiceRequestTablePageLoaderType;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { isTableLoading } = useRouteActivity();
  const data = loaderData.serviceRequests;
  const newRequestButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => navigate(ROUTES.serviceRequestNew)}
    >
      {t("Home_new_maintenance_request")}
    </Button>
  );

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[breadcrumbHomeItem, breadcrumbItem(t("Maintenance"))]}
      />
      <section style={{ marginTop: "2rem" }}>
        {data.length === 0 && !isTableLoading ? (
          <Empty
            description={t("Home_maintenance_empty")}
            style={{ marginTop: "3rem" }}
          >
            {newRequestButton}
          </Empty>
        ) : (
          <CustomTable
            tableHeaders={
              <TableHeaderBar
                left={
                  <TableHeaderInfo
                    title={t("Home_maintenance_page_title")}
                    count={data.length}
                    countLabel={t("Total_Records_Found")}
                  />
                }
                right={newRequestButton}
              />
            }
            mobileCardConfig={{ titleKey: "title", primaryKeys: ["status"] }}
            mobileDefaultSort={{ dataIndex: "createdAt", order: "descend" }}
            tableProps={{
              dataSource: loaderData.serviceRequests || [],
              columns: getServiceRequestTableColumns(t),
              rowKey: (record) => record.id,
              loading: isTableLoading,
            }}
          />
        )}
      </section>
    </>
  );
};
